<template>
  <page class="spec-spacing" :head-text="$t('login.cloudzet_welcome')" scroll-content>
    <div class="form-wrapper mt-5 pb-24">
      <!-- Gender -->
      <gender-selector show-caption v-model="local.gender" />
      <!-- Name -->
      <text-field
        class="w-full my-3"
        v-model.trim="local.nick_name"
        required
        :error.sync="nameError"
        :placeholder="$t('profile.name')"
      />
      <!--  Birthday -->
      <div class="w-full my-3 flex flex-row">
        <birth-year class="w-full" v-model="birthyear" :error.sync="birthyearError" />
      </div>
      <!--  Height -->
      <height class="w-full my-3" v-model="local.height" />
      <!--  Weight -->
      <weight class="w-full my-3" v-model="local.weight" />
      <!-- Measuring System -->
      <system-selector class="self-end my-3" />
      <!--  Save -->
      <c-button class="mt-10 my-auto md:mb-10" :confirmed-status="canSave" @click="setProfile">
        <span>{{ $t('profile.save') }}</span>
      </c-button>
    </div>
  </page>
</template>

<script>
import ApiActions from '@/modules/api-actions';

import Page from '@/components/common/Page.vue';
import TextField from '@/components/common/Fields/Text.vue';
import Height from '@/components/common/Fields/Height.vue';
import Weight from '@/components/common/Fields/Weight.vue';
import BirthYear from '@/components/common/Fields/BirthYear.vue';
import GenderSelector from '@/components/common/Fields/GenderSelector.vue';
import SystemSelector from '@/components/common/Fields/SystemSelector.vue';
import CButton from '@/components/common/CButton.vue';

const FIELDS = ['nick_name', 'gender'];

export default {
  name: 'SettingUserData',
  components: {
    Page,
    TextField,
    Height,
    Weight,
    BirthYear,
    GenderSelector,
    SystemSelector,
    CButton,
  },
  props: {
    profile: Object,
    embedded: {
      type: Boolean,
      default: false,
    },
  },
  mounted() {
    this.local = { ...this.profile };
    this.birthyear = parseInt(this.local.birthday, 10) || '';
  },
  data: () => ({
    local: {},
    nameError: false,
    birthyear: '',
    birthyearError: false,
  }),
  methods: {
    async setProfile() {
      if (!this.canSave || this.nameError) return;

      this.$events.startLoading();
      const req = {
        gender: this.local.gender,
        birthday: `${this.birthyear}-01-01`,
        nick_name: this.local.nick_name,
      };

      if (this.local.height) req.height = this.local.height;
      if (this.local.weight) req.weight = this.local.weight;

      try {
        await ApiActions.user.setProfile(req);
        if (this.embedded) {
          this.$emit('save');
        } else {
          this.$events.goto('HomePage');
        }
      } catch (e) {
        console.log(e.message);
        this.$events.alert(e.message);
      } finally {
        this.$events.stopLoading();
      }
    },
  },
  computed: {
    canSave() {
      const required = FIELDS.every(f => !!this.local[f]);
      if (this.birthyear) return required && !this.birthyearError;

      return required;
    },
  },
};
</script>

<style lang="scss" scoped>
// What the RWD spec draws for this screen. Kept under .spec-spacing so the
// selector beats common/Page.vue's own rules on specificity rather than on
// whichever file webpack emits first, and so /login — which shares the same
// .head — keeps the mark at its intrinsic size.
.spec-spacing {
  // Only the height is set, so the width follows the artwork instead of a second
  // number that could drift from the file.
  ::v-deep .head img {
    height: 40px;
  }

  // common/Page.vue stacks its mt-24 on top of the root's .safe-area padding
  // rather than composing with it, so on a device reporting a 62px top inset
  // the mark sat 178px down instead of the 96px a no-inset device gets. Absorb
  // the inset here instead, and use the offset the spec draws. Kept inside
  // .spec-spacing so /login, which renders the same head, is untouched.
  // Interpolated to keep sass out of max().
  ::v-deep .head {
    margin-top: #{'max(0px, calc(59px - var(--safe-top)))'};
  }
}

.form-wrapper {
  @apply flex flex-col items-center w-full flex-1 px-8 max-w-sm;
  & > *:not(:first-child) {
    max-height: 45px;
  }
  @screen md {
    @apply max-w-2xl;
  }
}
</style>
